import theme.DarkTheme
import theme.LightTheme
import theme.ThemeColors
import theme.ThemePreference
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement

/**
 * Web theme manager. Maintains the current theme and applies CSS variables.
 * Components read WebTheme.current.xxx for all colors.
 */
object WebTheme {
    var current: ThemeColors = DarkTheme
        private set

    fun initialize() {
        val saved = kotlinx.browser.localStorage.getItem("theme_preference") ?: ThemePreference.SYSTEM
        applyPreference(saved)

        // Listen for system theme changes
        window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", {
            val pref = kotlinx.browser.localStorage.getItem("theme_preference") ?: ThemePreference.SYSTEM
            if (pref == ThemePreference.SYSTEM) {
                applyPreference(pref)
            }
        })
    }

    fun applyPreference(preference: String) {
        kotlinx.browser.localStorage.setItem("theme_preference", preference)
        val isDark = when (preference) {
            ThemePreference.LIGHT -> false
            ThemePreference.DARK -> true
            else -> window.matchMedia("(prefers-color-scheme: dark)").matches
        }
        current = if (isDark) DarkTheme else LightTheme
        applyCSSVariables()
    }

    /** Force dark theme for marketing pages (does not persist preference). */
    fun forceDark() {
        current = DarkTheme
        applyCSSVariables()
    }

    /** Restore the user's saved preference (call when navigating to app pages). */
    fun restorePreference() {
        val saved = kotlinx.browser.localStorage.getItem("theme_preference") ?: ThemePreference.SYSTEM
        val isDark = when (saved) {
            ThemePreference.LIGHT -> false
            ThemePreference.DARK -> true
            else -> window.matchMedia("(prefers-color-scheme: dark)").matches
        }
        current = if (isDark) DarkTheme else LightTheme
        applyCSSVariables()
    }

    private fun applyCSSVariables() {
        val t = current
        val root = (document.documentElement as? HTMLElement)?.style ?: return

        root.setProperty("--bg", t.background)
        root.setProperty("--card", t.card)
        root.setProperty("--border", t.border)
        root.setProperty("--text-primary", t.textPrimary)
        root.setProperty("--text-secondary", t.textSecondary)
        root.setProperty("--accent", t.accent)
        root.setProperty("--accent-strong", t.accentStrong)
        root.setProperty("--income", t.incomeGreen)
        root.setProperty("--expense", t.expenseRed)
        root.setProperty("--target-orange", t.targetOrange)
        root.setProperty("--pro-badge", t.proBadge)
        root.setProperty("--pro-badge-text", t.proBadgeText)
        root.setProperty("--chip-sel-bg", t.chipSelectedBg)
        root.setProperty("--chip-sel-border", t.chipSelectedBorder)
        root.setProperty("--chip-unsel-bg", t.chipUnselectedBg)
        root.setProperty("--chip-unsel-border", t.chipUnselectedBorder)
        root.setProperty("--chip-unsel-text", t.chipUnselectedText)
        root.setProperty("--nav-bg", t.navBackground)
        root.setProperty("--nav-text", t.navText)
        root.setProperty("--nav-active", t.navActive)
        root.setProperty("--icon-circle-default", t.iconCircleDefault)
        root.setProperty("--icon-stroke", t.iconStroke)
        root.setProperty("--shadow", t.cardShadow)

        // Update body directly
        document.body?.style?.background = t.background
        document.body?.style?.color = t.textPrimary

        // Toggle body class for CSS-only theme hooks
        document.body?.classList?.let { classList ->
            if (t.isDark) {
                classList.remove("light-theme")
                classList.add("dark-theme")
            } else {
                classList.remove("dark-theme")
                classList.add("light-theme")
            }
        }
    }
}
